import './qr-code-modal.scss';

import { ReactComponent as CloseIcon } from '@basqet-dashboard/common/assets/icons/close.svg';
import { ReactComponent as CopyIcon } from '@basqet-dashboard/common/assets/icons/copy-icon.svg';
import { ReactComponent as DownloadIcon } from '@basqet-dashboard/common/assets/icons/download.svg';
import { Button } from '@basqet-dashboard/common/components';
import { UserContext } from '@basqet-dashboard/common/context';
import { copyToClipboard } from '@basqet-dashboard/common/utils';
import { ANALYTICS_EVENTS } from '@config';
import { useAnalytics } from '@hooks';
import html2canvas from 'html2canvas';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import QRCode from 'react-qr-code';

import { QRCodeReceipt } from './qr-code-receipt';

function QRCodeModal({ paymentUrl, onClose, title = 'Payment Link QR Code', paymentLinkId = '' }) {
  const { track } = useAnalytics();
  const qrCodeRef = useRef();
  const modalRef = useRef();
  const { user } = useContext(UserContext);
  const [isDownloading, setIsDownloading] = useState(false);

  const paymentLinkName = title.replace('QR Code - ', '') || null;
  const companyName = user?.merchantName || 'Basqet';

  useEffect(() => {
    track(ANALYTICS_EVENTS.PAYMENT_LINK_QR_CODE_VIEWED, { payment_link_id: paymentLinkId });
  }, [paymentLinkId, track]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    if (modalRef.current) {
      modalRef.current.focus();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  const downloadQRCode = async () => {
    track(ANALYTICS_EVENTS.PAYMENT_LINK_DOWNLOAD_QR_CODE_CLICKED, {
      payment_link_id: paymentLinkId
    });
    setIsDownloading(true);
    try {
      const receiptContainer = document.createElement('div');
      receiptContainer.id = 'temp-receipt-container';
      receiptContainer.style.position = 'absolute';
      receiptContainer.style.left = '-9999px';
      receiptContainer.style.top = '-9999px';
      receiptContainer.style.width = '600px';
      document.body.appendChild(receiptContainer);

      ReactDOM.render(
        <QRCodeReceipt
          paymentUrl={paymentUrl}
          paymentLinkName={paymentLinkName}
          companyName={companyName}
        />,
        receiptContainer
      );

      // needed to give the rendering function time to complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      const receiptElement = receiptContainer.querySelector('.qr-code-receipt');

      if (!receiptElement) {
        throw new Error('Receipt element not found');
      }

      const canvas = await html2canvas(receiptElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true
      });

      const pngFile = canvas.toDataURL('image/png', 1.0);
      const downloadLink = document.createElement('a');
      downloadLink.download = `basqet-payment-link-${Date.now()}.png`;
      downloadLink.href = pngFile;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      ReactDOM.unmountComponentAtNode(receiptContainer);
      document.body.removeChild(receiptContainer);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating receipt:', error);
      const svg = qrCodeRef.current.querySelector('svg');
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      canvas.width = 256;
      canvas.height = 256;

      img.onload = () => {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const pngFile = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.download = 'payment-link-qr-code.png';
        downloadLink.href = pngFile;
        downloadLink.click();
      };

      img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyUrl = () => {
    copyToClipboard('Payment link', paymentUrl).then(() => {
      track(ANALYTICS_EVENTS.PAYMENT_LINK_COPIED, { payment_link_id: paymentLinkId });
    });
  };

  return (
    <div className="qr-code-modal-overlay" role="dialog" aria-modal="true" tabIndex="-1">
      <div className="qr-code-modal" role="document" ref={modalRef} tabIndex="-1">
        <div className="qr-code-modal__header">
          <h3 className="qr-code-modal__title">{title}</h3>
          <button className="qr-code-modal__close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="qr-code-modal__content">
          <div className="qr-code-container" ref={qrCodeRef}>
            <QRCode
              value={paymentUrl}
              size={200}
              style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            />
          </div>

          <div className="qr-code-modal__url">
            <p className="url-text">{paymentUrl}</p>
          </div>

          <div className="qr-code-modal__actions">
            <Button variant="outline" isFull onClick={handleCopyUrl}>
              <CopyIcon /> Copy Link
            </Button>

            <Button variant="primary" isFull onClick={downloadQRCode} isLoading={isDownloading}>
              <DownloadIcon />
              Download QR
            </Button>

            <Button variant="outline" isFull onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

QRCodeModal.propTypes = {
  paymentUrl: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  paymentLinkId: PropTypes.string
};

QRCodeModal.defaultProps = {
  title: 'Payment Link QR Code',
  paymentLinkId: ''
};

export { QRCodeModal };
